import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ticketHref } from "./InboxThread";
import { Bell, At, Checks, ListChecks, ChatCircleText, ArrowBendUpLeft, UserCircleGear, CheckCircle } from "@phosphor-icons/react";

const TYPE_ICON = { mention: At, task: ListChecks, message: ChatCircleText, reply: ArrowBendUpLeft, assigned: UserCircleGear, task_update: CheckCircle };
import { api, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { POPUP_TYPES, announceMessage } from "./IncomingMessageHost";

// Shared across bell instances so each new item pops up only once.
const alerted = new Set();
let primed = false;
let soundOn = true;
let loadedFor = null;
export const setNotifySound = (on) => { soundOn = on; };

function softChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[880, 0], [1318.5, 0.12]].forEach(([f, t]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.35);
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.4);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) { /* audio blocked until user interacts */ }
}

const POLL_MS = 5000;
const listeners = new Set();
let latest = { items: [], unread: 0 };
let timer = null;
let navRef = null;

function handleFresh(items) {
  const fresh = items.filter((n) => !n.read && !alerted.has(n.id));
  fresh.forEach((n) => alerted.add(n.id));
  if (!primed) { primed = true; return; }
  if (!fresh.length) return;
  const popups = fresh.filter((n) => POPUP_TYPES.includes(n.type) && n.message_id);
  if (!popups.length) return;
  if (soundOn) softChime();
  popups.forEach(announceMessage);
}

export function refreshNotifications() {
  return api.get("/notifications/mine").then((r) => {
    latest = r.data;
    listeners.forEach((fn) => fn(latest));
    handleFresh(r.data.items);
  }).catch(() => {});
}

async function openNotification(n) {
  if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
  refreshNotifications();
  const focus = n.type === "mention" ? { note: n.note_id } : ["task", "task_update", "reply"].includes(n.type) ? { task: n.message_id } : {};
  navRef?.(n.ticket_id ? ticketHref(n.ticket_id, focus) : "/dashboard");
}

function subscribe(fn, userId) {
  if (loadedFor !== userId) {
    loadedFor = userId; primed = false; alerted.clear(); soundOn = true; latest = { items: [], unread: 0 };
    api.get("/users/me/prefs").then((r) => { soundOn = r.data.notify_sound; }).catch(() => {});
    refreshNotifications();
  }
  listeners.add(fn);
  fn(latest);
  if (!timer) timer = setInterval(refreshNotifications, POLL_MS);
  return () => {
    listeners.delete(fn);
    if (!listeners.size) { clearInterval(timer); timer = null; }
  };
}

export function NotificationBell({ panelClass = "" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(latest);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  navRef = nav;

  const load = refreshNotifications;
  useEffect(() => subscribe(setData, user?.id), [user?.id]);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const openItem = (n) => { setOpen(false); openNotification(n); };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => { setOpen((o) => !o); if (!open) load(); }} title="Notifications" data-testid="notification-bell"
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-[#EFF6FF] hover:text-[#1E5FEB] transition-colors">
        <Bell size={18} weight={data.unread ? "fill" : "bold"} className={data.unread ? "text-[#1E5FEB]" : ""} />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="notification-unread-count">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className={`absolute z-50 w-[340px] max-w-[calc(100vw-1rem)] rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_20px_48px_rgba(15,23,42,0.16)] overflow-hidden ${panelClass}`} data-testid="notification-panel">
          <div className="px-4 py-3 border-b border-[#F1F5F9] flex items-center justify-between">
            <span className="text-[13px] font-bold text-slate-900">Notifications</span>
            {data.unread > 0 && (
              <button onClick={readAll} data-testid="notification-read-all" className="text-[11.5px] font-semibold text-[#1E5FEB] inline-flex items-center gap-1 hover:underline">
                <Checks size={13} weight="bold" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[380px] overflow-y-auto divide-y divide-[#F1F5F9]">
            {data.items.length === 0 && <div className="px-4 py-8 text-center text-[13px] text-slate-500">Nothing new.</div>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 flex gap-3 transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-[#F5F9FF]"}`}>
                <span className="w-8 h-8 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">{(() => { const I = TYPE_ICON[n.type] || At; return <I size={15} weight="bold" className="text-[#1E5FEB]" />; })()}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] text-slate-800"><b>{n.title || `${n.by} mentioned you`}</b>{n.ticket_number ? <> on <b className="font-mono">{n.ticket_number}</b></> : null}{n.customer_name ? ` · ${n.customer_name}` : ""}</span>
                  <span className="block text-[12px] text-slate-500 line-clamp-2 mt-0.5">{n.text}</span>
                  <span className="block text-[10.5px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#1E5FEB] mt-1.5 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
